import { useState, useEffect, useCallback, useRef } from 'react';
import { Doughnut } from 'react-chartjs-2';
import Moment from 'react-moment';
import { Chart as ChartJS, ArcElement } from 'chart.js';

import { Button, Typography, IconButton, Table, TableContainer, TableHead, TableBody, Paper } from '@mui/material';
import { RemoveRedEye } from '@mui/icons-material';

import { C, D, Loading, TRow, TCell } from '../include';

import 'moment/locale/pt-br';

ChartJS.register(ArcElement);

// ==========================================
// COMPONENTE INTERNO: Equipament (Modal de Monitoramento)
// ==========================================
const Equipament = ({ api, dialogClose }) => {
  const [data, setData] = useState(null);
  
  // Ref para proteger o disparo inicial que gera o log
  const isFirstRender = useRef(true);

  const fetchData = useCallback((isFirstCall = false) => {
    api.get(`/server?log=${isFirstCall}`)
       .then(r => setData(r))
       .catch(err => console.error("Erro ao ler status do equipamento:", err));
  }, [api]);

  useEffect(() => {
    // 1. Faz a primeira chamada protegida: dispara log=true UMA ÚNICA vez
    if (isFirstRender.current) {
      fetchData(true);
      isFirstRender.current = false;
    }

    // 2. Registra o loop a cada 2 segundos (chamadas silenciosas, log=false)
    const interval = setInterval(() => {
      fetchData(false);
    }, 2000);

    // 3. Cleanup: O React rodará esta função quando o modal for desmontado,
    // matando o interval instantaneamente.
    return () => clearInterval(interval);
  }, [fetchData]);

  if (!data) {
    return (
      <D className="row e aC" style={{ minWidth: '200px', minHeight: '200px' }}>
        <Loading />
      </D>
    );
  }

  return (
    <D className="col e aF scD" style={{ padding: '2rem 4rem' }}>
      <D className="row w aC">
        <Typography variant="h6">{'Nome do host: ' + data.hostname}</Typography>
      </D>
      
      <D h="2rem" />
      
      <D className="row w aC">
        {`${data.so} ${data.kernel_version} -`}
        &nbsp;<Moment locale="en" parse="dddd D, MMMM YYYY" date={data.running_since} toNow ago />&nbsp;
        {`online - ${data.num_cpu_cores + 1} cores / ${data.num_processes} procs`}
      </D>
      
      <D className="row w aC"></D>
      <D h="2rem" />
      
      <D className="row w aC" style={{ padding: '1rem' }}>
        {/* Gráfico CPU */}
        <D className="col e aC" style={{ width: '80px' }}>
          <Typography variant="subtitle1" color="textSecondary">Processador</Typography>
          <Doughnut data={{ datasets: [{ data: [data.cpu_usage, 100 - data.cpu_usage], backgroundColor: ['#ffb300', '#eee'] }] }} />
          <Typography variant="subtitle1" color="textSecondary">{data.cpu_usage.toFixed(0) + '%'}</Typography>
        </D>
        <D size="2rem" />
        
        {/* Gráfico RAM */}
        <D className="col e aC" style={{ width: '80px' }}>
          <Typography variant="subtitle1" color="textSecondary">RAM</Typography>
          <Doughnut data={{ datasets: [{ data: [data.memo_ram_usage, data.memo_ram - data.memo_ram_usage], backgroundColor: ['#ffb300', '#eee'] }] }} />
          <Typography variant="subtitle1" color="textSecondary">{`${data.memo_ram_usage.toFixed(1)}/${data.memo_ram.toFixed(0)}`}</Typography>
        </D>
        <D size="2rem" />
        
        {/* Gráfico SWAP */}
        <D className="col e aC" style={{ width: '80px' }}>
          <Typography variant="subtitle1" color="textSecondary">Swap</Typography>
          <Doughnut data={{ datasets: [{ data: [data.memo_swap, data.memo_swap_usage - data.memo_swap], backgroundColor: ['#ffb300', '#eee'] }] }} />
          <Typography variant="subtitle1" color="textSecondary">{`${data.memo_swap.toFixed(1)}/${data.memo_swap_usage.toFixed(0)}`}</Typography>
        </D>
        <D size="2rem" />
        
        {/* Gráfico DISCO */}
        <D className="col e aC" style={{ width: '80px' }}>
          <Typography variant="subtitle1" color="textSecondary">Disco</Typography>
          <Doughnut data={{ datasets: [{ data: [data.disk_size_usage, data.disk_size - data.disk_size_usage], backgroundColor: ['#ffb300', '#eee'] }] }} />
          <Typography variant="subtitle1" color="textSecondary">{`${data.disk_size_usage.toFixed(1)}/${data.disk_size.toFixed(0)}`}</Typography>
        </D>
      </D>
      
      <D h="2rem" />
      
      <D className="row w aC">
        <Button variant="outlined" color="primary" onClick={dialogClose}>Fechar</Button>
      </D>
    </D>
  );
};


// ==========================================
// COMPONENTE PRINCIPAL: Servers
// ==========================================
export const Servers = ({ api, user, dialog, dialogClose }) => {
  const [servers, setServers] = useState(null); // null indica loading
  
  // Ref para proteger a montagem da tabela pai
  const isFirstRender = useRef(true);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    // Portão de entrada: Executa a chamada apenas uma vez na vida do componente
    if (isFirstRender.current) {
      isFirstRender.current = false;

      api.get('/Equipments')
         .then(r => {
           setServers(r.computers || []); // Define o estado diretamente, sem a trava isMounted
         })
         .catch(err => {
           console.error("Erro ao buscar lista de servidores:", err);
           setServers([]);
         });
    }
  }, [api]);

  return (
    <D className="col e aF scD" style={{ padding: '2rem' }}>
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer style={{ height: '100%' }}>
          <Table stickyHeader>
            <TableHead>
              <TRow>
                <TCell align="center">Parâmetros</TCell>
                <TCell align="center">Status</TCell>
                <TCell>ID</TCell>
                <TCell>Tipo</TCell>
                {isAdmin && (
                  <>
                    <TCell>IP</TCell>
                    <TCell>IP back-end</TCell>
                  </>
                )}
                <TCell width="100%">Nome do equipamento</TCell>
              </TRow>
            </TableHead>
            
            <TableBody>
              {servers === null ? (
                // Carga inicial
                <TRow>
                  <TCell component="th" align="center" colSpan={isAdmin ? 7 : 5}>
                    <Loading />
                  </TCell>
                </TRow>
              ) : servers.length > 0 ? (
                // Lista populada
                servers.map(row => (
                  <TRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    
                    <TCell align="center" component="th">
                      <IconButton 
                        style={{ color: '#aaa' }} 
                        onClick={() => dialog(
                          <Equipament 
                            api={api} 
                            row={row} 
                            dialogClose={dialogClose} 
                          />
                        )}
                      >
                        <RemoveRedEye />
                      </IconButton>
                    </TCell>
                    
                    <TCell component="th" align="center">
                      <span style={{ color: row.status === false ? 'red' : 'green', fontWeight: 'bold' }}>
                        {row.status === false ? 'Inativo' : 'Ativo'}
                      </span>
                    </TCell>
                    
                    <TCell component="th">{row.id}</TCell>
                    <TCell component="th">{row.type}</TCell>
                    
                    {isAdmin && (
                      <>
                        <TCell component="th">{row.ip}</TCell>
                        <TCell component="th">{C.baseURL ? new URL(C.baseURL).host : ''}</TCell>
                      </>
                    )}
                    
                    <TCell component="th">{row.alias}</TCell>
                  </TRow>
                ))
              ) : (
                // Tabela Vazia
                <TRow>
                  <TCell component="th" align="center" colSpan={isAdmin ? 7 : 5}>
                    Nenhum servidor encontrado
                  </TCell>
                </TRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </D>
  );
};